import { UseQueryOptions } from '@tanstack/react-query';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { HttpError } from '../Http/HttpError';
import { isCancelError } from '../Http/Http.types';

type UseQueryOptionsObject<TQueryFnData = unknown, TError = unknown> = Omit<
  UseQueryOptions<TQueryFnData, TError>,
  'queryKey' | 'queryFn'
>;

type QueryOptionsObject = Omit<UseQueryOptions<any, any, any, any>, 'queryKey' | 'queryFn'>;

type OptionsObject = {
  auth?: boolean;
} & QueryOptionsObject;

type DefaultOptionsFn = (options?: OptionsObject) => QueryOptionsObject;

const defaultOptions: DefaultOptionsFn = (options = { auth: false }) => {
  const { auth, retry, ...rest } = options;

  return {
    refetchOnReconnect: true,
    // Changed from true to false to prevent request storms when users return to tab after inactivity
    // This aligns with global React Query config and prevents intermittent 500 errors during session refresh
    // Critical features (notifications, comments) have explicit refetchOnWindowFocus: true overrides
    refetchOnWindowFocus: false,
    retryDelay: 1000,
    enabled: auth ? !AuthUtils.isExpired() : true,
    retry: (count, error: HttpError) => {
      if (isCancelError(error)) {
        return false;
      }

      if (error.code === 422) {
        return false;
      }

      if (!retry) {
        return false;
      }

      if (typeof retry === 'function') {
        return retry(count, error);
      }

      if (typeof retry === 'number') {
        return count < retry;
      }

      return false;
    },
    ...(rest ?? {}),
  };
};

export type { UseQueryOptionsObject };
export { defaultOptions };
